import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  PageHeader, Panel, Button, IconButton, Icon, Tag, Dialog, Field, TextInput, Select, Checkbox, Segmented, FileDrop,
  Banner, EmptyState, Spinner, Tooltip, ckConfirm, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { toHebrewNumeral } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import { ExamBlanksNext } from './ExamAssetsNext.jsx';
import ExamIssuanceWizard from './ExamIssuanceWizard.jsx';
import { Tabs, FormGrid, Row, apiError } from '../../next/ui';

/**
 * ספריית בחינות — new design. Same endpoints and actions as ExamLibrary: difficulties → parts/tractates →
 * simanim/perakim tree (create / delete at every level, ranges, named perakim), questions of the selected leaf
 * (add / edit with B·I·U, star, delete), Excel import, Word import (preview → pick → commit), templates,
 * the design-elements tab and the exam issuance wizard.
 * Master–detail: the tree on one side, the selected leaf's questions on the other (instead of a dialog).
 */
const ALLOWED = [['b', 'strong'], ['i', 'em'], ['u']];
function keepBIU(html, { editor = false } = {}) {
  if (!html) return '';
  let s = String(html);
  ALLOWED.forEach((tags, n) => {
    tags.forEach((t) => {
      s = s.replace(new RegExp(`<${t}[^>]*>`, 'gi'), `\x00${n}O\x00`).replace(new RegExp(`</${t}>`, 'gi'), `\x00${n}C\x00`);
    });
  });
  if (editor) s = s.replace(/<div>/gi, '\n').replace(/<\/div>/gi, '').replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<[^>]+>/gi, '');
  ['b', 'i', 'u'].forEach((t, n) => { s = s.replace(new RegExp(`\x00${n}O\x00`, 'g'), `<${t}>`).replace(new RegExp(`\x00${n}C\x00`, 'g'), `</${t}>`); });
  if (editor) s = s.replace(/&nbsp;/g, ' ').trim();
  return s;
}
const safeRender = (t) => keepBIU(t);
const sanitizeEditor = (h) => keepBIU(h, { editor: true });

const IS_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const MODE_LABEL = IS_GEMARA ? 'ספריית בחינות גמרא' : 'ספריית בחינות הלכה';
const HALACHA_PARTS = ['אורח חיים', 'יורה דעה', 'אבן העזר', 'חושן משפט'];
const TRACTATE_LIST = [
  'ברכות', 'שבת', 'עירובין', 'פסחים', 'יומא', 'סוכה', 'ביצה', 'ראש השנה', 'תענית', 'מגילה',
  'מועד קטן', 'חגיגה', 'יבמות', 'כתובות', 'נדרים', 'נזיר', 'סוטה', 'גיטין', 'קידושין',
  'בבא קמא', 'בבא מציעא', 'בבא בתרא', 'סנהדרין', 'מכות', 'שבועות', 'עבודה זרה', 'הוריות',
  'זבחים', 'מנחות', 'חולין', 'בכורות', 'ערכין', 'תמורה', 'כריתות', 'מעילה', 'תמיד', 'נידה',
];
const LEAF = IS_GEMARA ? 'פרק' : 'סימן';
const LEAVES = IS_GEMARA ? 'פרקים' : 'סימנים';
const leafNum = (v) => (IS_GEMARA && typeof v === 'object' ? v.perek : v);
const leafLabel = (v) => {
  if (IS_GEMARA && typeof v === 'object') return v.perekName ? `פרק ${toHebrewNumeral(v.perek)} — ${v.perekName}` : `פרק ${toHebrewNumeral(v.perek)}`;
  return `${LEAF} ${toHebrewNumeral(v)}`;
};
const leafParams = (leaf) => (IS_GEMARA ? { tractate: leaf.tractate, perek: leaf.perek } : { chelek: leaf.chelek, siman: leaf.siman });
const leafTitle = (leaf) => (IS_GEMARA
  ? `${leaf.tractate} פרק ${toHebrewNumeral(leaf.perek)}${leaf.perekName ? ` — ${leaf.perekName}` : ''}`
  : `${leaf.chelek} סימן ${toHebrewNumeral(leaf.siman)}`);

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** free-text combo over the tractate list */
function TractateInput({ value, onChange, autoFocus }) {
  return (
    <>
      <TextInput list="nx-tractates" value={value} onChange={(e) => onChange(e.target.value)} placeholder="שם מסכתא…" data-autofocus={autoFocus || undefined} />
      <datalist id="nx-tractates">{TRACTATE_LIST.map((t) => <option key={t} value={t} />)}</datalist>
    </>
  );
}

export default function ExamLibraryNext() {
  const toast = useCkToast();
  const [tab, setTab] = useState('questions');
  const [issuanceOpen, setIssuanceOpen] = useState(false);

  const [difficulties, setDifficulties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDiff, setOpenDiff] = useState(null);
  const [tree, setTree] = useState({});
  const [treeLoading, setTreeLoading] = useState(false);
  const [openLevel2, setOpenLevel2] = useState(null);
  const [leaf, setLeaf] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  const [diffDialog, setDiffDialog] = useState(null); // { name }
  const [rangeDialog, setRangeDialog] = useState(null); // { parent, from, to }
  const [perekDialog, setPerekDialog] = useState(null); // { parent, num, name }
  const [tractateDialog, setTractateDialog] = useState(null); // { name, mode, from, to, perakim }
  const [questionDialog, setQuestionDialog] = useState(null); // { editing, starred }
  const editorRef = useRef(null);

  const [importOpen, setImportOpen] = useState(false);
  const [imp, setImp] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  const loadDifficulties = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/exam-library/difficulties');
      setDifficulties(data || []);
    } catch {
      toast.error('שגיאה בטעינת רמות הקושי');
    } finally {
      setLoading(false);
    }
  }, [toast]);
  useEffect(() => { loadDifficulties(); }, [loadDifficulties]);

  const loadTree = useCallback(async (difficulty) => {
    setTreeLoading(true);
    try {
      const { data } = await api.get(`/exam-library/tree/${encodeURIComponent(difficulty)}`);
      setTree(data || {});
    } catch {
      toast.error('שגיאה בטעינת מבנה התיקיות');
    } finally {
      setTreeLoading(false);
    }
  }, [toast]);

  const loadQuestions = useCallback(async (difficulty, params) => {
    setQuestionsLoading(true);
    try {
      const { data } = await api.get(`/exam-library/questions/${encodeURIComponent(difficulty)}`, { params });
      setQuestions((data || []).filter((q) => q.questionText !== '__placeholder__'));
    } catch {
      toast.error('שגיאה בטעינת השאלות');
    } finally {
      setQuestionsLoading(false);
    }
  }, [toast]);

  const clearLeaf = () => { setLeaf(null); setQuestions([]); };
  const clickDiff = (diff) => {
    setOpenLevel2(null);
    clearLeaf();
    if (openDiff === diff) { setOpenDiff(null); setTree({}); return; }
    setOpenDiff(diff);
    loadTree(diff);
  };
  const clickLevel2 = (name) => { clearLeaf(); setOpenLevel2((cur) => (cur === name ? null : name)); };
  const isLeafOn = (parent, v) => !!leaf && (IS_GEMARA ? leaf.tractate === parent && leaf.perek === leafNum(v) : leaf.chelek === parent && leaf.siman === leafNum(v));
  const clickLeaf = (parent, v) => {
    if (isLeafOn(parent, v)) { clearLeaf(); return; }
    const next = IS_GEMARA
      ? { tractate: parent, perek: leafNum(v), perekName: typeof v === 'object' ? v.perekName : null }
      : { chelek: parent, siman: leafNum(v) };
    setLeaf(next);
    loadQuestions(openDiff, leafParams(next));
  };

  /* ── templates ── */
  const downloadTemplate = async (docx) => {
    try {
      const response = await api.get(docx ? '/exam-library/template-docx' : '/exam-library/template', { responseType: 'blob' });
      download(response.data, docx ? 'exam-library-template.docx' : 'exam-library-template.xlsx');
    } catch {
      toast.error(docx ? 'שגיאה בהורדת תבנית ה-Word' : 'שגיאה בהורדת התבנית');
    }
  };

  /* ── structure ── */
  const createDifficulty = async () => {
    if (!diffDialog?.name.trim()) return;
    try {
      await api.post('/exam-library/difficulties', { name: diffDialog.name.trim() });
      setDiffDialog(null);
      toast.success('רמת קושי נוצרה בהצלחה');
      loadDifficulties();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה ביצירת רמת קושי'));
    }
  };
  const deleteDifficulty = async (diff) => {
    if (!(await ckConfirm({ title: 'מחיקת רמת קושי', message: `למחוק את רמת הקושי "${diff}" וכל השאלות שבה?`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/exam-library/difficulties/${encodeURIComponent(diff)}`);
      toast.success('רמת קושי נמחקה');
      if (openDiff === diff) { setOpenDiff(null); setTree({}); clearLeaf(); }
      loadDifficulties();
    } catch {
      toast.error('שגיאה במחיקה');
    }
  };
  const deleteFolder = async (parent) => {
    const label = IS_GEMARA ? `מסכת "${parent}"` : `"${parent}"`;
    if (!(await ckConfirm({ title: 'מחיקת תיקייה', message: `למחוק את ${label} וכל הסימנים/פרקים והשאלות שבה?\nפעולה זו אינה הפיכה.`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete('/exam-library/folder', { params: { difficulty: openDiff, ...(IS_GEMARA ? { tractate: parent } : { chelek: parent }) } });
      if (leaf && (IS_GEMARA ? leaf.tractate : leaf.chelek) === parent) clearLeaf();
      if (openLevel2 === parent) setOpenLevel2(null);
      toast.success(IS_GEMARA ? 'מסכתא נמחקה' : 'חלק נמחק');
      loadTree(openDiff);
    } catch {
      toast.error('שגיאה במחיקה');
    }
  };
  const deleteLeaf = async (parent, v) => {
    const n = leafNum(v);
    if (!(await ckConfirm({ title: 'מחיקה', message: `למחוק את ${LEAF} ${toHebrewNumeral(n)} מ"${parent}"?\nכל השאלות שתחתיו יימחקו לצמיתות.`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete('/exam-library/leaf', { params: { difficulty: openDiff, ...(IS_GEMARA ? { tractate: parent, perek: n } : { chelek: parent, siman: n }) } });
      if (isLeafOn(parent, v)) clearLeaf();
      toast.success(IS_GEMARA ? 'פרק נמחק' : 'סימן נמחק');
      loadTree(openDiff);
    } catch {
      toast.error('שגיאה במחיקה');
    }
  };
  const openNewLeaf = (parent) => {
    if (IS_GEMARA) setPerekDialog({ parent, num: '', name: '' });
    else setRangeDialog({ parent, from: '', to: '' });
  };
  const createRange = async () => {
    const from = Number(rangeDialog.from);
    const to = Number(rangeDialog.to);
    if (!from || !to || from < 1 || to < from) { toast.error(IS_GEMARA ? 'טווח פרקים לא תקין' : 'טווח סימנים לא תקין'); return; }
    try {
      await api.post('/exam-library/range', { difficulty: openDiff, from, to, ...(IS_GEMARA ? { tractate: rangeDialog.parent } : { chelek: rangeDialog.parent }) });
      setRangeDialog(null);
      toast.success(IS_GEMARA ? 'פרקים נוספו בהצלחה' : 'סימנים נוספו בהצלחה');
      loadTree(openDiff);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהוספה'));
    }
  };
  const addSinglePerek = async () => {
    const num = parseInt(perekDialog.num, 10);
    if (!num || num < 1) { toast.error('מספר פרק חייב להיות 1 לפחות'); return; }
    try {
      await api.post('/exam-library/range', { difficulty: openDiff, tractate: perekDialog.parent, perakim: [{ perek: num, name: perekDialog.name.trim() || null }] });
      setPerekDialog(null);
      toast.success('פרק נוסף בהצלחה');
      loadTree(openDiff);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהוספת פרק'));
    }
  };
  const openNewTractate = () => setTractateDialog({ name: '', mode: 'range', from: '', to: '', perakim: [{ perek: '', name: '' }] });
  const createTractate = async () => {
    const d = tractateDialog;
    if (!d?.name.trim()) return;
    let body;
    if (d.mode === 'named') {
      const valid = d.perakim.filter((p) => p.perek || p.name?.trim());
      if (!valid.length) { toast.error('יש להוסיף לפחות פרק אחד'); return; }
      if (valid.some((p) => !p.perek || parseInt(p.perek, 10) < 1)) { toast.error('מספר פרק חייב להיות 1 לפחות'); return; }
      body = { difficulty: openDiff, tractate: d.name.trim(), perakim: valid.map((p) => ({ perek: parseInt(p.perek, 10), name: p.name?.trim() || null })) };
    } else {
      const from = Number(d.from);
      const to = Number(d.to);
      if (!from || from < 1) { toast.error('מספר פרק חייב להיות 1 לפחות'); return; }
      if (!to || to < from) { toast.error('פרק סיום חייב להיות גדול או שווה לפרק התחלה'); return; }
      body = { difficulty: openDiff, tractate: d.name.trim(), from, to };
    }
    try {
      await api.post('/exam-library/range', body);
      setTractateDialog(null);
      toast.success('מסכתא נוספה בהצלחה');
      loadTree(openDiff);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהוספת מסכתא'));
    }
  };

  /* ── questions ── */
  const editorRefCallback = useCallback((node) => {
    editorRef.current = node;
    if (!node) return;
    node.innerHTML = questionDialog?.editing ? (questionDialog.editing.questionText || '') : '';
    try {
      const range = document.createRange();
      range.selectNodeContents(node);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      node.focus();
    } catch { /* ignore */ }
  }, [questionDialog?.editing]); // eslint-disable-line react-hooks/exhaustive-deps
  const exec = (cmd) => (e) => { e.preventDefault(); editorRef.current?.focus(); document.execCommand(cmd, false, null); };

  const saveQuestion = async () => {
    const text = sanitizeEditor(editorRef.current?.innerHTML || '');
    if (!text) return;
    const { editing, starred } = questionDialog;
    try {
      if (editing) {
        await api.put(`/exam-library/questions/${editing._id}`, { questionText: text, starred, version: editing.version });
        toast.success('שאלה עודכנה בהצלחה');
      } else {
        await api.post('/exam-library/questions', { difficulty: openDiff, questionText: text, starred, ...leafParams(leaf) });
        toast.success('שאלה נוספה בהצלחה');
      }
      setQuestionDialog(null);
      loadQuestions(openDiff, leafParams(leaf));
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בשמירת שאלה'));
    }
  };
  const toggleStar = async (q) => {
    try {
      const { data } = await api.put(`/exam-library/questions/${q._id}`, { starred: !q.starred, version: q.version });
      setQuestions((prev) => prev.map((r) => (r._id === q._id ? { ...r, starred: data.starred, version: data.version } : r)));
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בעדכון כוכבית'));
    }
  };
  const deleteQuestion = async (q) => {
    if (!(await ckConfirm({ title: 'מחיקת שאלה', message: 'למחוק שאלה זו?', confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/exam-library/questions/${q._id}`);
      toast.success('שאלה נמחקה');
      loadQuestions(openDiff, leafParams(leaf));
    } catch {
      toast.error('שגיאה במחיקת שאלה');
    }
  };

  /* ── import (Excel / Word) ── */
  const freshImport = (source = 'excel') => ({ source, file: null, result: null, step: 0, preview: null, questions: [], difficulty: '', chelek: HALACHA_PARTS[0], tractate: '', perek: '' });
  const openImport = () => { setImp(freshImport()); setImportOpen(true); };
  const closeImport = () => { if (!importLoading) { setImportOpen(false); setImp(null); } };
  const setI = (patch) => setImp((s) => ({ ...s, ...(typeof patch === 'function' ? patch(s) : patch) }));
  const importExcel = async () => {
    if (!imp.file) return;
    setImportLoading(true);
    setI({ result: null });
    try {
      const fd = new FormData();
      fd.append('file', imp.file);
      const { data } = await api.post('/exam-library/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setI({ result: data, file: null });
      loadDifficulties();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בייבוא'));
    } finally {
      setImportLoading(false);
    }
  };
  const docxPreview = async () => {
    if (!imp.file) return;
    if (!imp.difficulty.trim()) { toast.error('יש לבחור רמת קושי'); return; }
    if (!IS_GEMARA && !imp.chelek) { toast.error('יש לבחור חלק'); return; }
    if (IS_GEMARA && !imp.tractate.trim()) { toast.error('יש לבחור מסכתא'); return; }
    setImportLoading(true);
    setI({ result: null });
    try {
      const fd = new FormData();
      fd.append('file', imp.file);
      const { data } = await api.post('/exam-library/import-docx/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setI((s) => ({ preview: data, step: 1, questions: (data.questions || []).map((q) => ({ ...q, include: true, editSiman: q.siman, editPerek: q.perek || (s.perek ? Number(s.perek) : '') })) }));
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בפיענוח קובץ הוורד'));
    } finally {
      setImportLoading(false);
    }
  };
  const docxCommit = async () => {
    const selected = imp.questions.filter((q) => q.include);
    if (!selected.length) { toast.error('לא נבחרו שאלות לייבוא'); return; }
    setImportLoading(true);
    try {
      const payload = {
        difficulty: imp.difficulty.trim(),
        questions: selected.map((q) => ({
          include: true,
          questionText: q.questionText,
          siman: Number(q.editSiman || q.siman),
          perek: IS_GEMARA ? Number(q.editPerek || imp.perek) : undefined,
          tractate: IS_GEMARA ? imp.tractate.trim() : undefined,
          starred: !!q.starred,
        })),
        ...(IS_GEMARA ? { tractate: imp.tractate.trim(), ...(imp.perek ? { perek: Number(imp.perek) } : {}) } : { chelek: imp.chelek }),
      };
      const { data } = await api.post('/exam-library/import-docx/commit', payload);
      setI({ result: data });
      toast.success(`יובאו ${data.inserted} שאלות מוורד`);
      loadDifficulties();
      if (openDiff === imp.difficulty.trim()) loadTree(openDiff);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בייבוא מוורד'));
    } finally {
      setImportLoading(false);
    }
  };
  const patchDocxQ = (idx, patch) => setI((s) => ({ questions: s.questions.map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));
  const includedCount = imp?.questions.filter((q) => q.include).length || 0;

  /* ── render ── */
  const level2Entries = IS_GEMARA ? Object.entries(tree) : Object.entries(tree).sort((a, b) => HALACHA_PARTS.indexOf(a[0]) - HALACHA_PARTS.indexOf(b[0]));
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  const treePanel = (
    <Panel
      title="מבנה הספרייה"
      sub={`${difficulties.length} רמות קושי`}
      icon="folder"
      flush
      actions={<Button size="sm" icon="plus" onClick={() => setDiffDialog({ name: '' })}>רמת קושי</Button>}
    >
      {loading ? <div className="ck-empty"><Spinner size={26} /></div> : !difficulties.length ? (
        <EmptyState icon="folder" title="אין עדיין רמות קושי" action={<Button icon="plus" onClick={() => setDiffDialog({ name: '' })}>רמת קושי חדשה</Button>}>
          לחץ על &quot;רמת קושי חדשה&quot; כדי להתחיל לבנות את הספרייה
        </EmptyState>
      ) : (
        <div className="nx-tree" role="tree">
          {difficulties.map((diff) => (
            <React.Fragment key={diff}>
              <div className={`nx-tree__row is-click ${openDiff === diff ? 'is-on' : ''}`} role="treeitem" aria-selected={openDiff === diff} aria-expanded={openDiff === diff} tabIndex={0} onClick={() => clickDiff(diff)} onKeyDown={(e) => { if (e.key === 'Enter') clickDiff(diff); }}>
                <Icon name={openDiff === diff ? 'down' : 'left'} size={14} />
                <Icon name="folder" size={16} />
                <b className="nx-grow">{diff}</b>
                <IconButton size="sm" icon="trash" label="מחק רמת קושי" onClick={stop(() => deleteDifficulty(diff))} />
              </div>
              {openDiff === diff ? (
                treeLoading ? <div className="nx-tree__row nx-tree__indent"><Spinner size={18} /></div> : (
                  <>
                    {!level2Entries.length ? <div className="nx-tree__row nx-tree__indent"><span className="ck-muted">{IS_GEMARA ? 'אין מסכתות עדיין' : 'אין תוכן עדיין'}</span></div> : null}
                    {level2Entries.map(([parent, leaves]) => (
                      <React.Fragment key={parent}>
                        <div className={`nx-tree__row nx-tree__indent is-click ${openLevel2 === parent ? 'is-on' : ''}`} role="treeitem" aria-selected={openLevel2 === parent} aria-expanded={openLevel2 === parent} tabIndex={0} onClick={() => clickLevel2(parent)} onKeyDown={(e) => { if (e.key === 'Enter') clickLevel2(parent); }}>
                          <Icon name={openLevel2 === parent ? 'down' : 'left'} size={14} />
                          <Icon name="book" size={16} />
                          <span className="nx-grow">{parent}</span>
                          <Tag>{`${leaves.length} ${LEAVES}`}</Tag>
                          <IconButton size="sm" icon="plus" label={IS_GEMARA ? 'הוסף פרק' : 'הוסף סימן'} onClick={stop(() => openNewLeaf(parent))} />
                          <IconButton size="sm" icon="trash" label={IS_GEMARA ? 'מחק מסכתא' : 'מחק חלק'} onClick={stop(() => deleteFolder(parent))} />
                        </div>
                        {openLevel2 === parent ? leaves.map((v) => (
                          <div key={leafNum(v)} className={`nx-tree__row nx-tree__indent2 is-click ${isLeafOn(parent, v) ? 'is-on' : ''}`} role="treeitem" aria-selected={isLeafOn(parent, v)} tabIndex={0} onClick={() => clickLeaf(parent, v)} onKeyDown={(e) => { if (e.key === 'Enter') clickLeaf(parent, v); }}>
                            <Icon name="file" size={14} />
                            <span className="nx-grow">{leafLabel(v)}</span>
                            <IconButton size="sm" icon="trash" label="מחק" onClick={stop(() => deleteLeaf(parent, v))} />
                          </div>
                        )) : null}
                      </React.Fragment>
                    ))}
                    {IS_GEMARA ? <div className="nx-tree__row nx-tree__indent"><Button size="sm" variant="ghost" icon="plus" onClick={openNewTractate}>הוסף מסכתא</Button></div> : null}
                  </>
                )
              ) : null}
            </React.Fragment>
          ))}
        </div>
      )}
    </Panel>
  );

  const questionsPanel = (
    <Panel
      title={leaf ? `שאלות — ${leafTitle(leaf)}` : 'שאלות'}
      sub={leaf ? `${openDiff} · ${questions.length} שאלות` : `בחרו ${LEAF} בעץ כדי לראות ולערוך את השאלות שלו`}
      icon="help"
      className={leaf ? 'nx-panel-on' : ''}
      actions={leaf ? <Button size="sm" variant="primary" icon="plus" onClick={() => setQuestionDialog({ editing: null, starred: false })}>הוסף שאלה</Button> : null}
    >
      {!leaf ? <EmptyState icon="help" compact title={`לא נבחר ${LEAF}`}>{`פתחו רמת קושי ו${IS_GEMARA ? 'מסכתא' : 'חלק'}, ולחצו על ${LEAF}.`}</EmptyState>
        : questionsLoading ? <div className="ck-empty"><Spinner size={26} /></div>
          : !questions.length ? <EmptyState icon="help" compact title="אין שאלות עדיין" action={<Button icon="plus" onClick={() => setQuestionDialog({ editing: null, starred: false })}>הוסף שאלה</Button>}>לחץ על &quot;הוסף שאלה&quot; כדי להתחיל</EmptyState>
            : (
              <div className="nx-list">
                {questions.map((q, idx) => (
                  <div key={q._id} className="nx-item" style={{ alignItems: 'flex-start' }}>
                    <Tag tone="primary">{idx + 1}</Tag>
                    {q.starred ? <b style={{ color: 'var(--ck-danger)' }}>*</b> : null}
                    <div className="nx-qtext" style={{ flex: 1 }} dangerouslySetInnerHTML={{ __html: safeRender(q.questionText) }} />
                    <div className="nx-item__acts">
                      <IconButton size="sm" icon="sparkle" active={!!q.starred} label={q.starred ? 'הסר כוכבית' : 'סמן בכוכבית'} onClick={() => toggleStar(q)} />
                      <IconButton size="sm" icon="edit" label="ערוך" onClick={() => setQuestionDialog({ editing: q, starred: !!q.starred })} />
                      <IconButton size="sm" icon="trash" label="מחק" onClick={() => deleteQuestion(q)} />
                    </div>
                  </div>
                ))}
              </div>
            )}
    </Panel>
  );

  return (
    <>
      <PageHeader
        title={MODE_LABEL}
        sub="שאלות, טפסי בסיס ודפים נוספים — והנפקת בחינות להדפסה"
        primary={{ label: 'הנפקת בחינות', icon: 'file', onClick: () => setIssuanceOpen(true) }}
        menus={tab === 'questions' ? [{
          key: 'io',
          label: 'ייבוא ותבניות',
          icon: 'upload',
          items: [
            { key: 'import', label: 'ייבוא שאלות (Excel / Word)', icon: 'upload', onClick: openImport },
            { key: 'tpl', label: 'הורד תבנית Excel', icon: 'download', onClick: () => downloadTemplate(false) },
            { key: 'tpl-docx', label: 'הורד תבנית Word', icon: 'download', onClick: () => downloadTemplate(true) },
          ],
        }] : undefined}
        more={tab === 'questions' ? [{ key: 'new-diff', label: 'רמת קושי חדשה', icon: 'folder', onClick: () => setDiffDialog({ name: '' }) }, { key: 'refresh', label: 'רענון', icon: 'refresh', onClick: loadDifficulties }] : undefined}
      />

      <Tabs value={tab} onChange={setTab} items={[{ value: 'questions', label: 'שאלות' }, { value: 'assets', label: 'אלמנטים עיצוביים' }]} />

      {tab === 'questions' ? <div className="nx-split">{treePanel}{questionsPanel}</div> : <ExamBlanksNext />}

      {/* difficulty */}
      <Dialog
        open={!!diffDialog}
        onClose={() => setDiffDialog(null)}
        title="רמת קושי חדשה"
        icon="folder"
        size="sm"
        footer={<><Button variant="ghost" onClick={() => setDiffDialog(null)}>ביטול</Button><Button variant="primary" onClick={createDifficulty} disabled={!diffDialog?.name.trim()}>צור</Button></>}
      >
        <Field label="שם רמת הקושי" hint={!IS_GEMARA ? 'ייווצרו אוטומטית 4 תיקיות: אורח חיים, יורה דעה, אבן העזר, חושן משפט' : undefined}>
          <TextInput value={diffDialog?.name || ''} placeholder='לדוג׳: "קל", "בינוני", "קשה"' data-autofocus onChange={(e) => setDiffDialog({ name: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') createDifficulty(); }} />
        </Field>
      </Dialog>

      {/* range of simanim */}
      <Dialog
        open={!!rangeDialog}
        onClose={() => setRangeDialog(null)}
        title={`הוספת ${LEAVES} ל${rangeDialog?.parent || ''}`}
        icon="plus"
        size="sm"
        footer={<><Button variant="ghost" onClick={() => setRangeDialog(null)}>ביטול</Button><Button variant="primary" icon="plus" onClick={createRange}>הוסף</Button></>}
      >
        <FormGrid cols={2}>
          <Field label={`מ${LEAF}`}><TextInput type="number" ltr min={1} data-autofocus value={rangeDialog?.from || ''} onChange={(e) => setRangeDialog((d) => ({ ...d, from: e.target.value }))} /></Field>
          <Field label={`עד ${LEAF}`} hint={rangeDialog?.from && rangeDialog?.to && Number(rangeDialog.to) >= Number(rangeDialog.from) ? `ייצרו ${Number(rangeDialog.to) - Number(rangeDialog.from) + 1} ${LEAVES}` : undefined}>
            <TextInput type="number" ltr min={1} value={rangeDialog?.to || ''} onChange={(e) => setRangeDialog((d) => ({ ...d, to: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') createRange(); }} />
          </Field>
        </FormGrid>
      </Dialog>

      {/* single perek (gemara) */}
      <Dialog
        open={!!perekDialog}
        onClose={() => setPerekDialog(null)}
        title={`הוספת פרק ל${perekDialog?.parent || ''}`}
        icon="plus"
        size="sm"
        footer={<><Button variant="ghost" onClick={() => setPerekDialog(null)}>ביטול</Button><Button variant="primary" icon="plus" onClick={addSinglePerek}>הוסף</Button></>}
      >
        <FormGrid cols={2}>
          <Field label="מספר פרק"><TextInput type="number" ltr min={1} data-autofocus value={perekDialog?.num || ''} onChange={(e) => setPerekDialog((d) => ({ ...d, num: e.target.value }))} /></Field>
          <Field label="שם פרק (אופציונלי)"><TextInput value={perekDialog?.name || ''} placeholder='לדוג׳: "מאימתי"' onChange={(e) => setPerekDialog((d) => ({ ...d, name: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') addSinglePerek(); }} /></Field>
        </FormGrid>
      </Dialog>

      {/* new tractate (gemara) */}
      <Dialog
        open={!!tractateDialog}
        onClose={() => setTractateDialog(null)}
        title="הוספת מסכתא חדשה"
        icon="book"
        size="md"
        footer={<><Button variant="ghost" onClick={() => setTractateDialog(null)}>ביטול</Button><Button variant="primary" icon="plus" disabled={!tractateDialog?.name.trim()} onClick={createTractate}>הוסף</Button></>}
      >
        {tractateDialog ? (
          <div className="nx-stack">
            <Field label="שם מסכתא"><TractateInput autoFocus value={tractateDialog.name} onChange={(v) => setTractateDialog((d) => ({ ...d, name: v }))} /></Field>
            <Segmented value={tractateDialog.mode} onChange={(v) => setTractateDialog((d) => ({ ...d, mode: v }))} items={[{ value: 'range', label: 'טווח מספרים' }, { value: 'named', label: 'פרקים עם שמות' }]} />
            {tractateDialog.mode === 'range' ? (
              <FormGrid cols={2}>
                <Field label="מפרק"><TextInput type="number" ltr min={1} value={tractateDialog.from} onChange={(e) => setTractateDialog((d) => ({ ...d, from: e.target.value }))} /></Field>
                <Field label="עד פרק" hint={tractateDialog.from && tractateDialog.to && Number(tractateDialog.to) >= Number(tractateDialog.from) ? `ייצרו ${Number(tractateDialog.to) - Number(tractateDialog.from) + 1} פרקים` : undefined}>
                  <TextInput type="number" ltr min={1} value={tractateDialog.to} onChange={(e) => setTractateDialog((d) => ({ ...d, to: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') createTractate(); }} />
                </Field>
              </FormGrid>
            ) : (
              <>
                <p className="nx-desc">הוסף פרקים עם שמות — ניתן להוסיף לא בסדר עולה, הסדר יישמר אוטומטית.</p>
                {tractateDialog.perakim.map((p, idx) => (
                  <Row key={idx}>
                    <TextInput type="number" ltr min={1} placeholder="מספר" style={{ width: 96 }} value={p.perek} onChange={(e) => setTractateDialog((d) => ({ ...d, perakim: d.perakim.map((x, i) => (i === idx ? { ...x, perek: e.target.value } : x)) }))} />
                    <TextInput placeholder='שם פרק — לדוג׳: "מאימתי"' style={{ flex: 1 }} value={p.name} onChange={(e) => setTractateDialog((d) => ({ ...d, perakim: d.perakim.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)) }))} />
                    {tractateDialog.perakim.length > 1 ? <IconButton size="sm" icon="trash" label="הסר פרק" onClick={() => setTractateDialog((d) => ({ ...d, perakim: d.perakim.filter((_, i) => i !== idx) }))} /> : null}
                  </Row>
                ))}
                <div><Button size="sm" variant="ghost" icon="plus" onClick={() => setTractateDialog((d) => ({ ...d, perakim: [...d.perakim, { perek: '', name: '' }] }))}>פרק נוסף</Button></div>
              </>
            )}
          </div>
        ) : null}
      </Dialog>

      {/* question editor */}
      <Dialog
        open={!!questionDialog}
        onClose={() => setQuestionDialog(null)}
        title={questionDialog?.editing ? 'עריכת שאלה' : 'הוספת שאלה'}
        icon="edit"
        size="md"
        footer={<><Button variant="ghost" onClick={() => setQuestionDialog(null)}>ביטול</Button><Button variant="primary" onClick={saveQuestion}>{questionDialog?.editing ? 'עדכן' : 'הוסף'}</Button></>}
      >
        {questionDialog ? (
          <div className="nx-stack">
            <div className="nx-rte">
              <div className="nx-rte__bar">
                <Tooltip title="הדגשה (Ctrl+B)"><button type="button" className="nx-rte__btn" onMouseDown={exec('bold')} aria-label="הדגשה"><Icon name="bold" size={16} /></button></Tooltip>
                <Tooltip title="נטוי (Ctrl+I)"><button type="button" className="nx-rte__btn" onMouseDown={exec('italic')} aria-label="נטוי"><Icon name="italic" size={16} /></button></Tooltip>
                <Tooltip title="קו תחתי (Ctrl+U)"><button type="button" className="nx-rte__btn" onMouseDown={exec('underline')} aria-label="קו תחתי"><Icon name="underline" size={16} /></button></Tooltip>
                <small className="ck-muted" style={{ marginInlineStart: 'auto' }}>סמן טקסט ולחץ B / I / U לעיצוב</small>
              </div>
              <div ref={editorRefCallback} className="nx-rte__body nx-qtext" contentEditable suppressContentEditableWarning dir="rtl" role="textbox" aria-multiline="true" aria-label="נוסח השאלה" />
            </div>
            <Checkbox checked={questionDialog.starred} onChange={(v) => setQuestionDialog((d) => ({ ...d, starred: v }))} label="סמן בכוכבית (למי שעושה כמה מבחנים)" />
          </div>
        ) : null}
      </Dialog>

      {/* import */}
      <Dialog
        open={importOpen}
        onClose={closeImport}
        title="ייבוא שאלות"
        icon="upload"
        size={imp?.source === 'word' && imp?.step === 1 ? 'lg' : 'md'}
        footer={imp ? (
          <>
            <Button variant="ghost" disabled={importLoading} onClick={closeImport}>סגור</Button>
            {imp.source === 'excel' ? <Button variant="primary" icon="upload" disabled={!imp.file || importLoading} onClick={importExcel}>{importLoading ? 'מייבא…' : 'ייבא'}</Button> : null}
            {imp.source === 'word' && imp.step === 0 ? <Button variant="primary" icon="right" disabled={!imp.file || importLoading || !imp.difficulty} onClick={docxPreview}>{importLoading ? 'מפענח…' : 'המשך לתצוגה מקדימה'}</Button> : null}
            {imp.source === 'word' && imp.step === 1 && !imp.result ? (
              <>
                <Button disabled={importLoading} onClick={() => setI({ step: 0, preview: null, questions: [] })}>חזרה</Button>
                <Button variant="primary" icon="upload" disabled={importLoading || !includedCount} onClick={docxCommit}>{importLoading ? 'מייבא…' : `ייבא ${includedCount} שאלות`}</Button>
              </>
            ) : null}
          </>
        ) : null}
      >
        {imp ? (
          <div className="nx-stack">
            <Segmented
              value={imp.source}
              onChange={(v) => { if (!importLoading) setImp(freshImport(v)); }}
              items={[{ value: 'excel', label: 'Excel' }, { value: 'word', label: 'Word' }]}
            />

            {imp.source === 'excel' ? (
              <>
                <p className="nx-desc">{`העלה קובץ Excel עם כותרות: ${IS_GEMARA ? 'רמת_קושי | מסכתא | שם_פרק | שאלה' : 'רמת_קושי | חלק | סימן | שאלה'}`}</p>
                {IS_GEMARA ? <small className="ck-muted">מספרי הפרקים יוקצו אוטומטית לפי סדר ההופעה. אם שם פרק כבר קיים במסכתא, השאלה תתווסף לפרק הקיים.</small> : null}
                <div><Button size="sm" variant="ghost" icon="download" onClick={() => downloadTemplate(false)}>הורד תבנית לדוגמה</Button></div>
                <FileDrop accept=".xlsx,.xls" icon="file" title={imp.file ? imp.file.name : 'בחר קובץ xlsx…'} hint="גררו לכאן או לחצו לבחירה" onFile={(f) => setI({ file: f || null, result: null })} />
              </>
            ) : null}

            {imp.source === 'word' && imp.step === 0 ? (
              <>
                <p className="nx-desc">העלה קובץ Word (.docx). המערכת מזהה כותרות «סימן …» או «סי&apos; …» ופסקאות שאלה, ומשמרת הדגשה וקו תחתי. שורות תשובה (קווים תחתיים) ידולגו אוטומטית.</p>
                <div><Button size="sm" variant="ghost" icon="download" onClick={() => downloadTemplate(true)}>הורד תבנית לדוגמה</Button></div>
                <FormGrid cols={2}>
                  <Field label="רמת קושי"><Select value={imp.difficulty} onChange={(v) => setI({ difficulty: v })} placeholder="בחרו רמת קושי" options={difficulties.map((d) => ({ value: d, label: d }))} /></Field>
                  {!IS_GEMARA
                    ? <Field label="חלק"><Select value={imp.chelek} onChange={(v) => setI({ chelek: v })} options={HALACHA_PARTS.map((p) => ({ value: p, label: p }))} /></Field>
                    : <Field label="מסכתא"><TractateInput value={imp.tractate} onChange={(v) => setI({ tractate: v })} /></Field>}
                  {IS_GEMARA ? <Field label="פרק (אופציונלי — ברירת מחדל לכל השאלות)"><TextInput type="number" ltr min={1} value={imp.perek} onChange={(e) => setI({ perek: e.target.value })} /></Field> : null}
                </FormGrid>
                <FileDrop accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" icon="file" title={imp.file ? imp.file.name : 'בחר קובץ docx…'} hint="גררו לכאן או לחצו לבחירה" onFile={(f) => setI({ file: f || null, result: null })} />
              </>
            ) : null}

            {imp.source === 'word' && imp.step === 1 ? (
              <>
                {imp.preview?.stats ? (
                  <Banner tone="info" icon="info">
                    {`נמצאו ${imp.preview.stats.questions} שאלות ב־${imp.preview.stats.uniqueSimanim || imp.preview.stats.simanim} סימנים · נבחרו ${includedCount} לייבוא · רמת קושי: ${imp.difficulty}${!IS_GEMARA ? ` · חלק: ${imp.chelek}` : ` · מסכתא: ${imp.tractate}`}`}
                  </Banner>
                ) : null}
                {imp.preview?.warnings?.length ? (
                  <Banner tone="warning" icon="alert">
                    <b>{`אזהרות (${imp.preview.warnings.length}):`}</b>
                    <div style={{ maxHeight: 72, overflowY: 'auto' }}>{imp.preview.warnings.map((w, i) => <div key={i}><small>{w}</small></div>)}</div>
                  </Banner>
                ) : null}
                <Row>
                  <Button size="sm" variant="ghost" icon="checkAll" onClick={() => setI((s) => ({ questions: s.questions.map((q) => ({ ...q, include: true })) }))}>בחר הכל</Button>
                  <Button size="sm" variant="ghost" icon="x" onClick={() => setI((s) => ({ questions: s.questions.map((q) => ({ ...q, include: false })) }))}>נקה בחירה</Button>
                </Row>
                <div className="nx-list" style={{ maxHeight: 420, overflowY: 'auto' }}>
                  {imp.questions.map((q, idx) => (
                    <div key={`${q.sourceParagraphIndex}-${idx}`} className={`nx-item ${q.include ? '' : 'is-off'}`} style={{ alignItems: 'flex-start' }}>
                      <Checkbox checked={!!q.include} onChange={(v) => patchDocxQ(idx, { include: v })} />
                      <div style={{ width: 92 }}>
                        <TextInput
                          type="number"
                          ltr
                          min={1}
                          aria-label={LEAF}
                          placeholder={LEAF}
                          value={(IS_GEMARA ? q.editPerek : q.editSiman) ?? ''}
                          onChange={(e) => { const v = e.target.value === '' ? '' : Number(e.target.value); patchDocxQ(idx, IS_GEMARA ? { editPerek: v } : { editSiman: v }); }}
                        />
                        {q.simanLabel && !IS_GEMARA ? <small className="ck-muted">{q.simanLabel}</small> : null}
                      </div>
                      <div className="nx-qtext" style={{ flex: 1 }}>
                        {q.starred ? <b style={{ color: 'var(--ck-danger)', marginInlineEnd: 4 }}>*</b> : null}
                        <span dangerouslySetInnerHTML={{ __html: safeRender(q.questionText) }} />
                      </div>
                      <IconButton size="sm" icon="sparkle" active={!!q.starred} label={q.starred ? 'הסר כוכבית' : 'סמן בכוכבית'} onClick={() => patchDocxQ(idx, { starred: !q.starred })} />
                    </div>
                  ))}
                  {!imp.questions.length ? <span className="ck-muted" style={{ padding: 12 }}>לא זוהו שאלות בקובץ</span> : null}
                </div>
              </>
            ) : null}

            {imp.result ? (
              <Banner tone={imp.result.errors?.length ? 'warning' : 'success'} icon={imp.result.errors?.length ? 'alert' : 'check'}>
                <b>{`יובאו ${imp.result.inserted} שאלות מתוך ${imp.result.total}`}</b>
                {imp.result.errors?.length ? <div style={{ maxHeight: 120, overflowY: 'auto', marginTop: 6 }}>{imp.result.errors.map((e, i) => <div key={i}><small>{e}</small></div>)}</div> : null}
              </Banner>
            ) : null}
          </div>
        ) : null}
      </Dialog>

      <ExamIssuanceWizard open={issuanceOpen} onClose={() => setIssuanceOpen(false)} />
    </>
  );
}

